import CircularProgress from "@mui/material/CircularProgress";
import {
  ArticleCategory,
  ArticleGroupIds,
  ArticleBrandId,
} from "entities/manageArticles/types";
import { StyledSpinnerWrapper } from "modules/manageArticles/styled";
import UnpublishedWarn from "modules/translations/components/UnpublishedWarn";
import { FC, useRef } from "react";
import {
  useNavigate,
  useBlocker,
  generatePath,
  useParams,
} from "react-router-dom";
import DraftEditor from "shared/components/DraftEditor";
import TextField from "shared/components/Form/TextField";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import { MANAGE_ARTICLES_GROUP_PATH } from "shared/routes/paths";
import { COLOR_BLACK } from "shared/theme/colors";
import ConfirmationModal from "./components/ConfirmationModal";
import { ArticlePageProvider, useArticlePage } from "./context";

const ArticleDetailsPage: FC = () => {
  const { brandId, categoryId, groupId } = useParams();

  const navigationInProgress = useRef(false);

  const {
    articleDetails,
    isLoadingArticle,
    version,
    setVersion,
    onSaveArticleDetails,
    editorContent,
    handleEditorChange,
    isDisabledSubmission,
    isSubmittingArticle,
    open,
    hasChanges,
    setHasChanges,
    setOpen,
    handleSaveDraft,
  } = useArticlePage();
  const navigate = useNavigate();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const isNavigatingAway = currentLocation.pathname !== nextLocation.pathname;

    if (hasChanges && isNavigatingAway && !navigationInProgress.current) {
      setOpen(true);
      return true;
    }

    return false;
  }) as { reset: () => void };

  const handleConfirm = () => {
    if (navigationInProgress.current) return;
    navigationInProgress.current = true;

    setOpen(false);
    setHasChanges(false);
    blocker.reset();

    setTimeout(() => {
      navigate(
        generatePath(MANAGE_ARTICLES_GROUP_PATH, {
          brandId: brandId as ArticleBrandId,
          categoryId: categoryId as ArticleCategory,
          groupId: groupId as ArticleGroupIds,
        }),
      );
    }, 100);
  };

  if (isLoadingArticle) {
    return (
      <StyledSpinnerWrapper>
        <CircularProgress />
      </StyledSpinnerWrapper>
    );
  }

  if (!articleDetails) {
    return null;
  }

  return (
    <FlexColumn rowGap={"1rem"}>
      <PageHeading hasBackButton customPageTitle={articleDetails.name}>
        <FlexCenter
          gap="1rem"
          alignItems="flex-start"
          mt="1rem"
          alignSelf="flex-end"
        >
          <PrimaryButton
            title="Save as draft"
            size="small"
            color="secondary"
            variant="outlined"
            disabled={isDisabledSubmission}
            onClick={handleSaveDraft}
            loading={isSubmittingArticle}
          />
          <FlexColumn
            justifyContent="flex-start"
            alignItems="flex-start"
            rowGap="0.25rem"
            position="relative"
          >
            <PrimaryButton
              title="Publish Changes"
              size="small"
              disabled={isDisabledSubmission}
              loading={isSubmittingArticle}
              onClick={onSaveArticleDetails}
            />
            {!isDisabledSubmission && !isSubmittingArticle && (
              <UnpublishedWarn />
            )}
          </FlexColumn>
        </FlexCenter>
      </PageHeading>
      <TextField
        id="version"
        label="Version"
        placeholder="Version"
        required
        labelColor={COLOR_BLACK}
        value={version}
        onChange={(e) => setVersion(e.target.value)}
      />
      <DraftEditor
        content={articleDetails.content?.editorContent ?? editorContent}
        label="Article content"
        onChange={handleEditorChange}
        placeholder="Enter article content..."
        hasHeadingToolbars
        hasListToolbars
        hasWidgets
        hasImageUpload
        hasVideoEmbed
      />
      <ConfirmationModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
      />
    </FlexColumn>
  );
};

const ArticleDetailsPageContainer = () => {
  return (
    <ArticlePageProvider>
      <ArticleDetailsPage />
    </ArticlePageProvider>
  );
};

export default ArticleDetailsPageContainer;
